import { Controller } from "@hotwired/stimulus"
import { track as mixpanelTrack } from "../lib/mixpanel"

// Phase 2.5 Tier 1 — Fund Source list + detail page controller.
//
// List page: forwards filter dropdown changes to the form's GET submit.
// Detail page: opens / closes five modals (add appropriation, edit
// appropriation, delete appropriation, expire fund source, cancel fund
// source) with backdrop dismiss and `stopPropagation` on the panel.
// Re-checks pending-flash from sessionStorage on reconnect (e.g.,
// after a state-transition redirect), and mirrors state-transition
// Mixpanel events to the client side.
export default class extends Controller {
  static targets = [
    "newFundSourceModal",
    "detailModal",
    "appropriationModal",
    "editAppropriationModal", "editAppropriationForm",
    "editAppropriationAmount", "editAppropriationEffectiveAt",
    "editAppropriationReference",
    "deleteAppropriationModal", "deleteAppropriationForm",
    "expireModal", "cancelModal",
    // Phase 2.5 Tier 2 — allocations tab modals.
    "allocateModal",
    "reserveModal", "reserveForm", "reserveProject",
    "releaseModal", "releaseForm", "releaseProject",
    // Phase 2.5 Tier 3 — chip-based S3 upload for appropriation docs.
    "appropriationDropzone", "appropriationFilePicker",
    "appropriationChipList", "appropriationHiddenInputs",
    "appropriationSubmitBtn"
  ]
  static values = { id: Number }

  connect() {
    this._showPendingFlash()
    this._appropriationAttachmentIds = []
    this._appropriationPendingUploads = 0
  }

  submitFilter(event) {
    event.currentTarget?.form?.submit()
  }

  // ── New fund source (index page modal) ──────────────────────────
  showNewFundSourceModal() {
    this._show(this.newFundSourceModalTarget)
    mixpanelTrack("Fund Source New Modal Opened")
  }

  hideNewFundSourceModal() {
    this._hide(this.newFundSourceModalTarget)
  }

  // ── Detail modal (index → row click → Turbo Frame fetch) ────────
  // The row link carries `data-turbo-frame="fund_source_modal"`, so
  // Turbo handles the fetch + content swap. This method just opens
  // the panel that hosts the frame so the user sees the loading state
  // until the response arrives.
  showDetailModal() {
    this._show(this.detailModalTarget)
    mixpanelTrack("Fund Source Detail Modal Opened")
  }

  hideDetailModal() {
    // Look up the modal globally rather than via this.detailModalTarget
    // — the close button can be triggered from inside the show content
    // (which has its own nested fund-source controller scope that
    // doesn't see the index page's target).
    const modal = document.querySelector('[data-fund-source-target="detailModal"]')
    if (!modal) return
    modal.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
    const frame = modal.querySelector("turbo-frame#fund_source_modal")
    if (frame) frame.innerHTML = `<div class="text-center text-sm text-gray-400 py-8">Loading…</div>`
  }

  // ── Add appropriation ───────────────────────────────────────────
  showAppropriationModal() {
    this._show(this.appropriationModalTarget)
    if (this.idValue) {
      mixpanelTrack("Fund Source Appropriation Modal Opened", {
        fund_source_id: this.idValue
      })
    }
  }

  hideAppropriationModal() {
    this._hide(this.appropriationModalTarget)
  }

  // ── Edit appropriation ──────────────────────────────────────────
  showEditAppropriationModal(event) {
    if (!this.hasEditAppropriationModalTarget) return
    const trigger = event.currentTarget
    const editUrl = trigger.dataset.appropriationEditUrl
    if (!editUrl) return

    this.editAppropriationFormTarget.action = editUrl
    if (this.hasEditAppropriationAmountTarget) {
      this.editAppropriationAmountTarget.value = trigger.dataset.appropriationAmount || ""
    }
    if (this.hasEditAppropriationEffectiveAtTarget) {
      const dateInput = this.editAppropriationEffectiveAtTarget
      dateInput.value = trigger.dataset.appropriationEffectiveAt || ""
      // Refresh flatpickr if it has wrapped this input.
      if (dateInput._flatpickr) dateInput._flatpickr.setDate(dateInput.value, false)
    }
    if (this.hasEditAppropriationReferenceTarget) {
      this.editAppropriationReferenceTarget.value = trigger.dataset.appropriationReference || ""
    }

    this._show(this.editAppropriationModalTarget)
  }

  hideEditAppropriationModal() {
    this._hide(this.editAppropriationModalTarget)
  }

  // ── Delete appropriation ────────────────────────────────────────
  showDeleteAppropriationModal(event) {
    if (!this.hasDeleteAppropriationModalTarget) return
    const deleteUrl = event.currentTarget.dataset.appropriationDeleteUrl
    if (!deleteUrl) return
    this.deleteAppropriationFormTarget.action = deleteUrl
    this._show(this.deleteAppropriationModalTarget)
  }

  hideDeleteAppropriationModal() {
    this._hide(this.deleteAppropriationModalTarget)
  }

  // ── Expire fund source ──────────────────────────────────────────
  showExpireModal() {
    this._show(this.expireModalTarget)
  }

  hideExpireModal() {
    this._hide(this.expireModalTarget)
  }

  // ── Cancel fund source (with reason) ────────────────────────────
  showCancelModal() {
    this._show(this.cancelModalTarget)
  }

  hideCancelModal() {
    this._hide(this.cancelModalTarget)
  }

  // ── Phase 2.5 Tier 2 — allocations tab modals ───────────────────
  showAllocateModal() {
    if (!this.hasAllocateModalTarget) return
    this._show(this.allocateModalTarget)
    if (this.idValue) {
      mixpanelTrack("Fund Allocate Modal Opened", {
        fund_source_id: this.idValue
      })
    }
  }

  hideAllocateModal() {
    if (!this.hasAllocateModalTarget) return
    this._hide(this.allocateModalTarget)
  }

  showReserveModal(event) {
    if (!this.hasReserveModalTarget) return
    const trigger = event.currentTarget
    this.reserveFormTarget.action = trigger.dataset.allocationReserveUrl || ""
    if (this.hasReserveProjectTarget) {
      const proj = trigger.dataset.allocationProject || ""
      this.reserveProjectTarget.textContent = proj ? `— ${proj}` : ""
    }
    this._show(this.reserveModalTarget)
  }

  hideReserveModal() {
    this._hide(this.reserveModalTarget)
  }

  showReleaseModal(event) {
    if (!this.hasReleaseModalTarget) return
    const trigger = event.currentTarget
    this.releaseFormTarget.action = trigger.dataset.allocationReleaseUrl || ""
    if (this.hasReleaseProjectTarget) {
      const proj = trigger.dataset.allocationProject || ""
      this.releaseProjectTarget.textContent = proj ? `— ${proj}` : ""
    }
    this._show(this.releaseModalTarget)
  }

  hideReleaseModal() {
    this._hide(this.releaseModalTarget)
  }

  // Stops modal-internal clicks from bubbling to the backdrop close
  // handler.
  stopPropagation(event) {
    event.stopPropagation()
  }

  // ── Helpers ─────────────────────────────────────────────────────
  _show(modal) {
    if (!modal) return
    modal.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  _hide(modal) {
    if (!modal) return
    modal.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  // Pull pending toast text out of sessionStorage (set before a
  // navigation that ended in `redirect_to`) and surface it as a flash
  // toast. Mirrors fee_schedule_controller's `_showPendingFlash`.
  _showPendingFlash() {
    const pending = sessionStorage.getItem("conslogFundSourcePendingFlash")
    if (!pending) return
    sessionStorage.removeItem("conslogFundSourcePendingFlash")
    try {
      const { type, message } = JSON.parse(pending)
      this._showToast(message, type || "notice")
    } catch (_e) {
      // Malformed payload — silently ignore.
    }
  }

  _showToast(message, type = "notice") {
    const div = document.createElement("div")
    div.className =
      type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    div.dataset.controller = "flash"
    div.dataset.flashDismissAfterValue = "5000"
    div.textContent = message
    document.body.prepend(div)
  }

  // ── Phase 2.5 Tier 3 — Appropriation supporting-doc uploads ──────
  // Shared with the FundAppropriation modal. Mirrors the chip-based
  // direct-S3 upload pattern used by `capital_request_form_controller`
  // and `report_form_controller`. Files become orphan Attachments on
  // register; `FundAppropriationsController#create` reparents them
  // when the form posts.

  dragEnterAppropriation(e) {
    e.preventDefault()
    this._setAppropriationDropzoneActive(true)
  }

  dragOverAppropriation(e) {
    e.preventDefault()
    this._setAppropriationDropzoneActive(true)
  }

  dragLeaveAppropriation(e) {
    if (e.relatedTarget && this.appropriationDropzoneTarget.contains(e.relatedTarget)) return
    this._setAppropriationDropzoneActive(false)
  }

  dropAppropriationFiles(e) {
    e.preventDefault()
    this._setAppropriationDropzoneActive(false)
    const files = Array.from(e.dataTransfer.files || [])
    if (files.length) this._uploadAppropriationFiles(files)
  }

  openAppropriationPicker() {
    if (this.hasAppropriationFilePickerTarget) this.appropriationFilePickerTarget.click()
  }

  appropriationFilesPicked(e) {
    const files = Array.from(e.target.files || [])
    if (files.length) this._uploadAppropriationFiles(files)
    e.target.value = ""
  }

  async _uploadAppropriationFiles(files) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    this._appropriationPendingUploads += files.length
    if (this.hasAppropriationSubmitBtnTarget) this.appropriationSubmitBtnTarget.disabled = true

    const placeholders = files.map(file => {
      const chip = this._createAppropriationChip(file.name, { uploading: true })
      this.appropriationChipListTarget.appendChild(chip)
      return chip
    })

    try {
      const presignRes = await fetch("/fund_sources/presign_appropriation_uploads", {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: files.map(f => ({
            filename: f.name,
            content_type: f.type || "application/octet-stream",
            byte_size: f.size
          }))
        })
      })

      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}))
        throw new Error(err.error || "Failed to get upload URLs")
      }

      const { uploads } = await presignRes.json()

      const results = await Promise.allSettled(
        uploads.map(async (upload, i) => {
          const file = files[i]
          const res = await fetch(upload.upload_url, {
            method: "PUT",
            headers: { "Content-Type": file.type || upload.content_type },
            body: file
          })
          if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`)
          return upload
        })
      )

      const successful = results
        .map(r => r.status === "fulfilled" ? r.value : null).filter(Boolean)

      if (successful.length === 0) throw new Error("All uploads failed")

      const registerRes = await fetch("/fund_sources/register_appropriation_uploads", {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: successful.map(u => ({ cache_name: u.cache_name, filename: u.filename }))
        })
      })

      if (!registerRes.ok) throw new Error("Failed to register uploads")
      const registered = await registerRes.json()

      placeholders.forEach(p => p.remove())
      registered.forEach(att => {
        this._appropriationAttachmentIds.push(att.id)
        const chip = this._createAppropriationChip(att.filename || `File #${att.id}`, {
          removable: true,
          onRemove: () => {
            chip.remove()
            this._appropriationAttachmentIds =
              this._appropriationAttachmentIds.filter(id => id !== att.id)
            this._syncAppropriationHidden()
          }
        })
        this.appropriationChipListTarget.appendChild(chip)
      })
      this._syncAppropriationHidden()
    } catch (e) {
      console.error("[fund-source] appropriation upload error:", e)
      placeholders.forEach(p => p.remove())
      window.alert(`Upload failed: ${e.message || e}`)
    } finally {
      this._appropriationPendingUploads -= files.length
      if (this._appropriationPendingUploads <= 0) {
        this._appropriationPendingUploads = 0
        if (this.hasAppropriationSubmitBtnTarget) this.appropriationSubmitBtnTarget.disabled = false
      }
    }
  }

  _syncAppropriationHidden() {
    if (!this.hasAppropriationHiddenInputsTarget) return
    this.appropriationHiddenInputsTarget.innerHTML = ""
    this._appropriationAttachmentIds.forEach(id => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "attachment_ids[]"
      input.value = id
      this.appropriationHiddenInputsTarget.appendChild(input)
    })
  }

  _setAppropriationDropzoneActive(active) {
    if (!this.hasAppropriationDropzoneTarget) return
    this.appropriationDropzoneTarget.classList.toggle("border-blue-400", active)
    this.appropriationDropzoneTarget.classList.toggle("bg-blue-50", active)
    this.appropriationDropzoneTarget.classList.toggle("border-gray-200", !active)
  }

  _createAppropriationChip(name, { removable = false, uploading = false, onRemove = null } = {}) {
    const chip = document.createElement("div")
    chip.className = `inline-flex items-center gap-1.5 pl-2 ${removable ? "pr-1" : "pr-2"} py-1 rounded-md text-xs ${uploading ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-700"}`

    const icon = uploading
      ? `<svg class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>`
      : `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg>`

    const truncated = name.length > 28
      ? name.slice(0, 24) + "..." + name.slice(name.lastIndexOf("."))
      : name

    let removeBtn = ""
    if (removable) {
      removeBtn = `<button type="button" class="ml-0.5 p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600" data-remove-chip>
        <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>`
    }

    chip.innerHTML = `${icon}<span class="truncate max-w-[180px]" title="${name}">${truncated}</span>${removeBtn}`

    if (removable && onRemove) {
      chip.querySelector("[data-remove-chip]")?.addEventListener("click", e => {
        e.stopPropagation()
        onRemove()
      })
    }

    return chip
  }
}
